import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";
import { extractFunctionsErrorMessage } from "../lib/functionsError";
import { Input } from "@core/components/ui/input";
import { Textarea } from "@core/components/ui/textarea";
import { Button } from "@core/components/ui/button";
import { Label } from "@core/components/ui/label";

interface Profile {
  slug: string | null;
  bio: string | null;
}

export function SettingsPersonalPage() {
  const { t } = useTranslation(["settings", "common"]);
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile>({ slug: null, bio: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [slugInput, setSlugInput] = useState("");
  const [bioInput, setBioInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  async function fetchProfile() {
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "user-slug",
        { method: "GET" },
      );
      if (invokeError) throw invokeError;
      setProfile(data);
      if (data.slug) setSlugInput(data.slug);
      if (data.bio) setBioInput(data.bio);
    } catch (err) {
      setError(t("settings:errorFetchingProfile"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveSlug() {
    if (!slugInput.trim()) {
      setError(t("settings:slugRequired"));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "user-slug",
        { method: "POST", body: { slug: slugInput, bio: profile.bio } },
      );

      if (invokeError) {
        setError(
          await extractFunctionsErrorMessage(
            invokeError,
            t("settings:errorSavingSlug"),
          ),
        );
        return;
      }

      setProfile({ ...profile, slug: data.slug });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t("settings:errorSavingSlug"));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveBio() {
    if (bioInput.length > 160) {
      setError(t("settings:bioTooLong"));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "user-slug",
        { method: "POST", body: { slug: profile.slug, bio: bioInput } },
      );

      if (invokeError) {
        setError(
          await extractFunctionsErrorMessage(
            invokeError,
            t("settings:errorSavingBio"),
          ),
        );
        return;
      }

      setProfile({ ...profile, bio: data.bio ?? bioInput });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t("settings:errorSavingBio"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground">{t("settings:loading")}</p>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Label className="text-sm font-medium text-foreground">
          {t("settings:yourPublicSlug")}
        </Label>

        <div className="flex gap-2">
          <span className="flex items-center px-2 text-muted-foreground">
            @
          </span>
          <Input
            type="text"
            value={slugInput}
            onChange={(e) => {
              setSlugInput(e.target.value.toLowerCase());
              setError(null);
            }}
            placeholder={t("settings:slugPlaceholder")}
            disabled={saving}
            className="flex-1"
            data-testid="personal-page-slug-input"
          />
          <Button
            onClick={handleSaveSlug}
            disabled={saving || slugInput === profile.slug}
            data-testid="personal-page-slug-save"
          >
            {saving ? t("common:saving") : t("common:save")}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          {t("settings:slugHelp")}
        </p>

        {profile.slug && (
          <div className="rounded-md border border-border bg-muted/50 p-3">
            <p className="mb-1 text-xs text-muted-foreground">
              {t("settings:yourPublicPage")}
            </p>
            <p className="text-sm font-medium text-primary">
              doodates.com/@{profile.slug}
            </p>
            <a
              href={`/@${profile.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-sm text-primary underline-offset-2 hover:text-primary/80 hover:underline"
            >
              {t("settings:viewPage")} →
            </a>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium text-foreground">
          {t("settings:bio")}
        </Label>

        <Textarea
          value={bioInput}
          onChange={(e) => {
            setBioInput(e.target.value.slice(0, 160));
            setError(null);
          }}
          placeholder={t("settings:bioPlaceholder")}
          disabled={saving}
          className="h-20"
          data-testid="personal-page-bio-input"
        />

        <p className="text-xs text-muted-foreground">
          {t("settings:bioHelp")}: {bioInput.length}/160
        </p>

        <Button
          onClick={handleSaveBio}
          disabled={saving || bioInput === profile.bio}
          data-testid="personal-page-bio-save"
        >
          {saving ? t("common:saving") : t("common:save")}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success">
          {t("common:saved")}
        </div>
      )}
    </div>
  );
}
